import { effect, inject } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivateFn, Router, RouterStateSnapshot, UrlTree } from '@angular/router';
import { AppConfigService, Log } from '@core/services/config/app-config.service';
import { catchError, map, Observable, of } from 'rxjs';
import { UserStoreService } from '@core/services/storage/user/user-store.service';
import { PaymentResultService } from '@components/checkout/services/payment-result.service';

/**
 * AuthGuard
 * versione con monitoraggio dei cambiamenti
 * Questo guardia verifica se l'utente è loggato.
 * Se l'utente non è loggato, reindirizza l'utente alla pagina di login.
 * 
 */
export const authGuard: CanActivateFn = (route: ActivatedRouteSnapshot, state: RouterStateSnapshot) => {
    const userStoreService = inject(UserStoreService);
    const router = inject(Router);
    const appConfig = inject(AppConfigService);

    if(appConfig.ssr()) {
        return false;
    }

    if (userStoreService.isLoggedAndNoGuestSignal()) {
        return true;
    } else {
        return router.createUrlTree(['/login'], {
            queryParams: { returnUrl: state.url }
        });
    }
};
/**
 * AuthGuard or GuestGuard
 * versione con monitoraggio dei cambiamenti
 * Questo guardia verifica se l'utente sia loggato o sia un guest.
 * Se l'utente non è loggato, reindirizza l'utente alla pagina di login.
 * 
 */
export const authOrGuestGuard: CanActivateFn = (route: ActivatedRouteSnapshot, state: RouterStateSnapshot) => {
    const userStoreService = inject(UserStoreService);
    const router = inject(Router);
    const appConfig = inject(AppConfigService);

    if(appConfig.ssr()) {
        return false;
    }

    if (userStoreService.isLoggedSignal() || userStoreService.isGuestSignal()) {
        return true;
    } else {
        return router.createUrlTree(['/login'], {
            queryParams: { returnUrl: state.url }
        });
    }
};
/**
 * GuestGuard
 * versione con monitoraggio dei cambiamenti
 * Questo guardia verifica che l'utente non sia loggato o sia guest.
 * Se l'utente è loggato, reindirizza l'utente alla dashboard.
 * 
 */
export const guestGuard: CanActivateFn = (route: ActivatedRouteSnapshot, state: RouterStateSnapshot) => {
    const userStoreService = inject(UserStoreService);
    const router = inject(Router);
    const appConfig = inject(AppConfigService);

    if(appConfig.ssr()) {
        return true; // never logged on server
    }

    if (!userStoreService.isLoggedSignal() || userStoreService.isGuestSignal()) {
        return true;
    } else {
        return router.createUrlTree(['/userarea/dashboard'], {
            queryParams: { returnUrl: state.url }
        });
    }
};

/**
 * guestCheckoutPaymentGuard
 * versione con monitoraggio dei cambiamenti
 * Questo verifica che l'utente sia guest.
 * Se l'utente è loggato non guest, reindirizza l'utente a checkout/pagamento.
 * 
 */
export const guestCheckoutPaymentGuard: CanActivateFn = (route, state) => {
    const userStoreService = inject(UserStoreService);
    const router = inject(Router);
    const appConfig = inject(AppConfigService);

    if (appConfig.ssr()) return false;

    // Solo guest possono accedere
    if (userStoreService.isGuestSignal()) return true;

    // Loggato non guest → manda al pagamento normale
    if (userStoreService.isLoggedAndNoGuestSignal()) {
        return router.createUrlTree(['/checkout/pagamento']);
    }

    // Non loggato → login
    return router.createUrlTree(['/login']);
};

/**
 * logoutGuestGuard
 * effettua logout per gli utente guest
 * 
 */
export const logoutGuestGuard: CanActivateFn = (route, state) => {
    const userStoreService = inject(UserStoreService);
    const appConfig = inject(AppConfigService);
    
    if (appConfig.ssr()) return false;

    if (userStoreService.isGuestSignal()) {
        return userStoreService.logout().pipe(
            map(() => true),
            catchError(() => of(true))
        );
    }

    return true;
};

/**
 * paymentResultGuard
 * verifica esistenza payment result in session storage
 * 
 */
export const paymentResultGuard: CanActivateFn = (route, state) => {
    const appConfig = inject(AppConfigService);
    const paymentResultService = inject(PaymentResultService);
    const router = inject(Router);
    
    if (appConfig.ssr()) return false;

    if (paymentResultService.hasResult()) {
        return true;
    }

    return router.createUrlTree(['/home']);
};